import { useState, useEffect, useCallback } from 'react';
import type { Workout, MonthlySummary } from '../../domain/workouts/types';
import {
  getUserWorkouts,
  getMonthlyBests,
} from '../../infrastructure/repositories/workoutRepository';
import { getPreviousMonthKey } from '../../domain/leaderboard/types';
import { buildMonthlySummary } from '../../domain/workouts/monthlyBests';

/** @deprecated use buildMonthlySummary directly */
export function buildSummaryFromWorkouts(workouts: Workout[]): MonthlySummary {
  return buildMonthlySummary(workouts);
}

export interface MonthlyHistory {
  workouts: Workout[];
  summary: MonthlySummary | null;
  previousSummary: MonthlySummary | null;
  isLoading: boolean;
  reload: () => Promise<void>;
}

/**
 * Loads a user's workouts and monthly bests for the given month, plus the
 * previous month's summary (used for the total delta). Shared by the history
 * page and the leaderboard user-history modal so both render identical data.
 */
export function useMonthlyHistory(userId: string | undefined, monthKey: string): MonthlyHistory {
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [summary, setSummary] = useState<MonthlySummary | null>(null);
  const [previousSummary, setPreviousSummary] = useState<MonthlySummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadData = useCallback(async (month: string, uid: string) => {
    const monthWorkouts = await getUserWorkouts(uid, month);

    let currentBests: MonthlySummary | null = null;
    let prevBests: MonthlySummary | null = null;

    try {
      currentBests = await getMonthlyBests(uid, month);
    } catch (err) {
      console.warn('getMonthlyBests current month failed, building from workouts', err);
    }

    if (!currentBests || (!currentBests.totalPoints && monthWorkouts.length > 0)) {
      currentBests = buildSummaryFromWorkouts(monthWorkouts);
    }

    try {
      const prevMonthWorkouts = await getUserWorkouts(uid, getPreviousMonthKey(month));
      prevBests = buildSummaryFromWorkouts(prevMonthWorkouts);
    } catch (err) {
      console.warn('getMonthlyBests prev month failed', err);
    }

    return {
      workouts: monthWorkouts,
      summary: currentBests,
      previousSummary: prevBests,
    };
  }, []);

  const reload = useCallback(async () => {
    if (!userId) return;
    setIsLoading(true);
    try {
      const data = await loadData(monthKey, userId);
      setWorkouts(data.workouts);
      setSummary(data.summary);
      setPreviousSummary(data.previousSummary);
    } catch (error) {
      console.error('Failed to load history data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userId, monthKey, loadData]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!userId) {
        if (!cancelled) setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const data = await loadData(monthKey, userId);
        if (!cancelled) {
          setWorkouts(data.workouts);
          setSummary(data.summary);
          setPreviousSummary(data.previousSummary);
        }
      } catch (error) {
        if (!cancelled) console.error('Failed to load history data:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [userId, monthKey, loadData]);

  return { workouts, summary, previousSummary, isLoading, reload };
}
